import { useState, useEffect } from "react";
import {
  DollarSign,
  CreditCard,
  AlertCircle,
  CheckCircle,
  Clock,
  Plus,
  Receipt,
  TrendingUp,
  Calendar,
  Download,
  ChevronDown,
  ChevronUp,
  Maximize2,
  X,
} from "lucide-react";
import medicalDB from "../services/database";

const PatientFinancialSummary = ({ patientId, onRecordPayment }) => {
  const [financialData, setFinancialData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (patientId) {
      loadFinancialData();
    }
  }, [patientId]);

  const loadFinancialData = async () => {
    try {
      setLoading(true);
      const [balance, patientTransactions] = await Promise.all([
        medicalDB.getPatientBalance(patientId),
        medicalDB.getPatientTransactions(patientId),
      ]);

      setFinancialData(balance);
      setTransactions(patientTransactions.slice(0, 5)); // Show last 5 transactions
    } catch (error) {
      console.error("Error loading financial data:", error);
    } finally {
      setLoading(false);
    }
  };

  const exportPatientStatement = async () => {
    try {
      const patient = await medicalDB.getPatient(patientId);
      const allTransactions = await medicalDB.getPatientTransactions(patientId); // Get all transactions for export

      const csvContent = [
        ["Patient Statement", "", "", "", "", ""],
        [`Patient: ${patient?.name || "Unknown"}`, "", "", "", "", ""],
        [
          `Generated: ${new Date().toLocaleDateString("en-IL")}`,
          "",
          "",
          "",
          "",
          "",
        ],
        ["", "", "", "", "", ""],
        [
          "Receipt Number",
          "Date",
          "Description",
          "Total Amount (₪)",
          "Paid Amount (₪)",
          "Outstanding (₪)",
        ],
        ...allTransactions.map((t) => [
          t.receiptNumber,
          new Date(t.date).toLocaleDateString("en-IL"),
          t.items?.map((item) => item.name).join(", ") || "Payment",
          t.totalAmount.toFixed(2),
          t.paidAmount.toFixed(2),
          (t.totalAmount - t.paidAmount).toFixed(2),
        ]),
        ["", "", "", "", "", ""],
        ["SUMMARY", "", "", "", "", ""],
        [
          "Total Owed:",
          "",
          "",
          financialData?.totalOwed?.toFixed(2) || "0.00",
          "",
          "",
        ],
        [
          "Total Paid:",
          "",
          "",
          financialData?.totalPaid?.toFixed(2) || "0.00",
          "",
          "",
        ],
        [
          "Outstanding Balance:",
          "",
          "",
          financialData?.balance?.toFixed(2) || "0.00",
          "",
          "",
        ],
      ]
        .map((row) => row.map((cell) => `"${cell}"`).join(","))
        .join("\n");

      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `patient-statement-${
        patient?.name?.replace(/\s+/g, "-") || "unknown"
      }-${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting patient statement:", error);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case medicalDB.PAYMENT_STATUS?.PAID || "paid":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case medicalDB.PAYMENT_STATUS?.PARTIAL || "partial":
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case medicalDB.PAYMENT_STATUS?.PENDING || "pending":
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case medicalDB.PAYMENT_STATUS?.OVERDUE || "overdue":
        return <AlertCircle className="h-4 w-4 text-red-600" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case medicalDB.PAYMENT_STATUS?.PAID || "paid":
        return "text-green-600 bg-green-50";
      case medicalDB.PAYMENT_STATUS?.PARTIAL || "partial":
        return "text-yellow-600 bg-yellow-50";
      case medicalDB.PAYMENT_STATUS?.PENDING || "pending":
        return "text-red-600 bg-red-50";
      case medicalDB.PAYMENT_STATUS?.OVERDUE || "overdue":
        return "text-red-700 bg-red-100";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  const formatPaymentMethod = (method) => {
    switch (method) {
      case medicalDB.PAYMENT_METHODS?.CASH || "cash":
        return "Cash";
      case medicalDB.PAYMENT_METHODS?.CARD || "card":
        return "Card";
      case medicalDB.PAYMENT_METHODS?.CHECK || "check":
        return "Check";
      case medicalDB.PAYMENT_METHODS?.BANK_TRANSFER || "bank_transfer":
        return "Bank Transfer";
      default:
        return method;
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          <div className="grid grid-cols-3 gap-4">
            <div className="h-16 bg-gray-200 rounded"></div>
            <div className="h-16 bg-gray-200 rounded"></div>
            <div className="h-16 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!financialData) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="text-center text-gray-500">
          <DollarSign className="h-8 w-8 mx-auto mb-2 text-gray-400" />
          <p>No financial data available</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Compact Header with Summary */}
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-blue-600" />
              <h3 className="text-base font-semibold text-gray-900">
                Financial Summary
              </h3>
            </div>
            <div className="flex gap-2">
              <button
                onClick={onRecordPayment}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Payment</span>
              </button>
              <button
                onClick={() => setShowModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 text-sm rounded-lg hover:bg-gray-50 transition-colors"
                title="View full details"
              >
                <Maximize2 className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Details</span>
              </button>
            </div>
          </div>

          {/* Compact Summary View */}
          <div className="grid grid-cols-2 gap-3 mb-3">
            {/* Outstanding Balance - Primary Focus */}
            <div
              className={`rounded-lg p-3 ${
                financialData.balance > 0 ? "bg-red-50" : "bg-green-50"
              }`}
            >
              <p
                className={`text-xs font-medium mb-1 ${
                  financialData.balance > 0 ? "text-red-600" : "text-green-600"
                }`}
              >
                Outstanding
              </p>
              <p
                className={`text-xl font-bold ${
                  financialData.balance > 0 ? "text-red-900" : "text-green-900"
                }`}
              >
                ₪{Math.abs(financialData.balance).toFixed(2)}
              </p>
            </div>

            {/* Total Transactions */}
            <div className="rounded-lg p-3 bg-gray-50">
              <p className="text-xs font-medium text-gray-600 mb-1">
                Total Owed
              </p>
              <p className="text-xl font-bold text-gray-900">
                ₪{financialData.totalOwed.toFixed(2)}
              </p>
            </div>
          </div>

          {/* Transaction Count & Status */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">
              {financialData.transactions} transaction{financialData.transactions !== 1 ? 's' : ''}
            </span>
            {financialData.balance > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                <AlertCircle className="h-3 w-3" />
                Payment Due
              </span>
            )}
          </div>

          {/* Expand/Collapse Button */}
          {transactions.length > 0 && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="w-full mt-3 flex items-center justify-center gap-2 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <span>
                {isExpanded ? "Hide" : "Show"} Recent Transactions
              </span>
              {isExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </button>
          )}
        </div>

        {/* Expandable Recent Transactions */}
        {isExpanded && transactions.length > 0 && (
          <div className="border-t border-gray-200 p-4 space-y-2 bg-gray-50">
            {transactions.map((transaction) => (
              <div
                key={transaction.id}
                className="flex items-center justify-between p-3 bg-white rounded-lg shadow-sm"
              >
                <div className="flex items-center gap-2 min-w-0">
                  {getStatusIcon(transaction.status)}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm text-gray-900 truncate">
                        {transaction.receiptNumber}
                      </span>
                      <span
                        className={`px-1.5 py-0.5 text-xs font-medium rounded-full whitespace-nowrap ${getStatusColor(
                          transaction.status
                        )}`}
                      >
                        {transaction.status}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">
                      {new Date(transaction.date).toLocaleDateString("en-IL")}
                    </div>
                  </div>
                </div>
                <div className="text-right ml-2">
                  <div className="font-medium text-sm text-gray-900 whitespace-nowrap">
                    ₪{transaction.totalAmount.toFixed(2)}
                  </div>
                  {transaction.paidAmount < transaction.totalAmount && (
                    <div className="text-xs text-gray-500">
                      Paid: ₪{transaction.paidAmount.toFixed(2)}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Full Details Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <DollarSign className="h-6 w-6 text-blue-600" />
                <h3 className="text-xl font-semibold text-gray-900">
                  Financial Details
                </h3>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={exportPatientStatement}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Download className="h-4 w-4" />
                  Export
                </button>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto flex-1">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                {/* Total Owed */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <TrendingUp className="h-8 w-8 text-blue-600" />
                    <div>
                      <p className="text-sm font-medium text-blue-600">
                        Total Owed
                      </p>
                      <p className="text-2xl font-bold text-blue-900">
                        ₪{financialData.totalOwed.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Total Paid */}
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-8 w-8 text-green-600" />
                    <div>
                      <p className="text-sm font-medium text-green-600">
                        Total Paid
                      </p>
                      <p className="text-2xl font-bold text-green-900">
                        ₪{financialData.totalPaid.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Outstanding Balance */}
                <div
                  className={`rounded-lg p-4 ${
                    financialData.balance > 0 ? "bg-red-50" : "bg-gray-50"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <AlertCircle
                      className={`h-8 w-8 ${
                        financialData.balance > 0
                          ? "text-red-600"
                          : "text-gray-600"
                      }`}
                    />
                    <div>
                      <p
                        className={`text-sm font-medium ${
                          financialData.balance > 0
                            ? "text-red-600"
                            : "text-gray-600"
                        }`}
                      >
                        Outstanding
                      </p>
                      <p
                        className={`text-2xl font-bold ${
                          financialData.balance > 0
                            ? "text-red-900"
                            : "text-gray-900"
                        }`}
                      >
                        ₪{Math.abs(financialData.balance).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* All Transactions */}
              {transactions.length > 0 ? (
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-900 flex items-center gap-2">
                    <Receipt className="h-4 w-4" />
                    Recent Transactions
                  </h4>
                  <div className="space-y-2">
                    {transactions.map((transaction) => (
                      <div
                        key={transaction.id}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          {getStatusIcon(transaction.status)}
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-gray-900">
                                {transaction.receiptNumber}
                              </span>
                              <span
                                className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                                  transaction.status
                                )}`}
                              >
                                {transaction.status}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <Calendar className="h-3 w-3" />
                              {new Date(transaction.date).toLocaleDateString(
                                "en-IL"
                              )}
                              <span>•</span>
                              <CreditCard className="h-3 w-3" />
                              {formatPaymentMethod(transaction.paymentMethod)}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-medium text-gray-900">
                            ₪{transaction.totalAmount.toFixed(2)}
                          </div>
                          <div className="text-sm text-gray-500">
                            Paid: ₪{transaction.paidAmount.toFixed(2)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Receipt className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p>No transactions yet</p>
                  <p className="text-sm">
                    Transactions will appear here after visits are billed
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PatientFinancialSummary;
